/**
 * Number helpers shared by every chart geometry: finite checks, clamping, rounding for SVG output and the summary
 * statistics of a series. Pure and DOM-free.
 */
/** A value a chart can draw: a finite number. `null`, `undefined`, `NaN` and `±Infinity` are "no data". */
export function isFiniteNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
}
export function clamp(value, min, max) {
    if (min > max)
        return clamp(value, max, min);
    return Math.min(max, Math.max(min, value));
}
export function lerp(from, to, t) {
    return from + (to - from) * t;
}
/** Round a coordinate to `digits` decimals so SVG path strings stay short and stable (`-0` becomes `0`). */
export function roundCoord(value, digits = 2) {
    const f = 10 ** digits;
    const r = Math.round(value * f) / f;
    return r === 0 ? 0 : r;
}
/** The min and max of the finite values, or `null` when there is none. */
export function extent(values) {
    let min = Infinity;
    let max = -Infinity;
    for (const v of values) {
        if (!isFiniteNumber(v))
            continue;
        if (v < min)
            min = v;
        if (v > max)
            max = v;
    }
    return min === Infinity ? null : { min, max };
}
/** Summary statistics of a series, or `null` when it has nothing drawable. Gaps (`null`) are skipped, not counted as zero. */
export function seriesStats(values) {
    let count = 0;
    let sum = 0;
    let min = Infinity;
    let max = -Infinity;
    let minIndex = -1;
    let maxIndex = -1;
    let first = 0;
    let last = 0;
    values.forEach((v, i) => {
        if (!isFiniteNumber(v))
            return;
        if (count === 0)
            first = v;
        last = v;
        count += 1;
        sum += v;
        if (v < min) {
            min = v;
            minIndex = i;
        }
        if (v > max) {
            max = v;
            maxIndex = i;
        }
    });
    if (count === 0)
        return null;
    return { count, sum, min, max, minIndex, maxIndex, first, last, delta: last - first };
}
/** `part / whole` as a fraction in [0, 1]; 0 when the whole is not positive (never NaN or Infinity). */
export function fraction(part, whole) {
    if (!isFiniteNumber(part) || !isFiniteNumber(whole) || whole <= 0)
        return 0;
    return clamp(part / whole, 0, 1);
}
//# sourceMappingURL=numbers.js.map